import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';

const AdminProducts = () => {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('todos');
  const [filterType, setFilterType] = useState('todos');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_usd: '',
    price_wholesale_usd: '',
    min_wholesale_qty: 1,
    units_per_package: 1,
    wholesale_only: false,
    category: 'pizza',
    badge: '',
    icon: '🍞',
    is_available: true,
    preparation_time: 30,
    ingredients: ''
  });

  // ============================================================
  // ICONOS DISPONIBLES POR CATEGORÍA
  // ============================================================
  const iconOptions = {
    pizza: ['🍕', '🥧', '🍽️', '🔥', '🧀'],
    brownie: ['🍫', '🧁', '🍰', '🎂', '🍩'],
    galleta: ['🍪', '🥠', '🍩', '🧁', '🍬'],
    pan: ['🥖', '🍞', '🥐', '🥯', '🥨'],
    croissant: ['🥐', '🥖', '🥯', '🍞', '🥨'],
    otros: ['🍽️', '🍴', '🥄', '🍰', '🎂', '🍮', '🍨', '🍧', '🍩', '🧁', '🍫', '🍬', '🍭', '📦', '🎁']
  };

  const allIcons = [
    '🍕', '🍫', '🍪', '🥖', '🥐', '🍞', '🥯', '🥨', '🧁', '🍰',
    '🎂', '🍮', '🍩', '🍨', '🍧', '🥧', '🍬', '🍭', '🍯', '🥞',
    '📦', '🎁', '🧺', '🛒', '📊', '💼', '🏪', '🎯', '⭐', '🌟'
  ];

  // ============================================================
  // OBTENER PRODUCTOS (CON adminView=true)
  // ============================================================
  const fetchProducts = async () => {
    try {
      setLoading(true);
      // ✅ adminView=true → el backend NO filtra productos exclusivos
      const { data } = await api.get('/products', {
        params: { adminView: 'true' }
      });
      console.log('📦 Productos obtenidos (admin):', data.data?.length);
      setProducts(data.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const getIconForCategory = (category) => {
    const defaults = {
      pizza: '🍕',
      brownie: '🍫',
      galleta: '🍪',
      pan: '🥖',
      croissant: '🥐',
      otros: '🍽️'
    };
    return defaults[category] || '🍞';
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        price_usd: product.price_usd,
        price_wholesale_usd: product.price_wholesale_usd || '',
        min_wholesale_qty: product.min_wholesale_qty || 1,
        units_per_package: product.units_per_package || 1,
        wholesale_only: product.wholesale_only === 1 || product.wholesale_only === true,
        category: product.category,
        badge: product.badge || '',
        icon: product.icon || getIconForCategory(product.category),
        is_available: product.is_available === 1,
        preparation_time: product.preparation_time || 30,
        ingredients: Array.isArray(product.ingredients) ? product.ingredients.join(', ') : ''
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price_usd: '',
        price_wholesale_usd: '',
        min_wholesale_qty: 1,
        units_per_package: 1,
        wholesale_only: false,
        category: 'pizza',
        badge: '',
        icon: '🍕',
        is_available: true,
        preparation_time: 30,
        ingredients: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setFormData(prev => ({
      ...prev,
      category,
      icon: getIconForCategory(category)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        priceUSD: parseFloat(formData.price_usd),
        priceWholesaleUSD: formData.price_wholesale_usd ? parseFloat(formData.price_wholesale_usd) : null,
        minWholesaleQty: parseInt(formData.min_wholesale_qty) || 1,
        unitsPerPackage: parseInt(formData.units_per_package) || 1,
        wholesaleOnly: formData.wholesale_only,
        category: formData.category,
        badge: formData.badge,
        icon: formData.icon,
        isAvailable: formData.is_available,
        preparationTime: parseInt(formData.preparation_time),
        ingredients: formData.ingredients.split(',').map(i => i.trim()).filter(i => i)
      };

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
      } else {
        await api.post('/products', payload);
      }

      handleCloseModal();
      fetchProducts();
    } catch (error) {
      alert('Error al guardar: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await api.delete(`/products/${id}`);
      fetchProducts();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleAvailability = async (product) => {
    try {
      await api.put(`/products/${product.id}`, {
        isAvailable: product.is_available !== 1
      });
      fetchProducts();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const categories = ['todos', 'pizza', 'brownie', 'galleta', 'pan', 'croissant', 'otros'];

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === 'todos' || p.category === filterCategory;
    const matchType = filterType === 'todos' || 
                      (filterType === 'wholesale' && p.wholesale_only) ||
                      (filterType === 'general' && !p.wholesale_only);
    return matchSearch && matchCategory && matchType;
  });

  const formatPrice = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '0.00';
    return num.toFixed(2);
  };

  const counts = {
    todos: products.length,
    wholesale: products.filter(p => p.wholesale_only).length,
    general: products.filter(p => !p.wholesale_only).length
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif font-bold">📦 Gestionar Productos</h1>
          <p className="text-gray-500 text-sm mt-1">
            {products.length} productos en total · {counts.wholesale} exclusivos mayorista
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-gold text-white px-5 py-2.5 rounded-full font-semibold hover:bg-gold-dark transition shadow-md flex items-center gap-2 text-sm"
        >
          <span>+</span> Nuevo Producto
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="🔍 Buscar producto..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-3 py-2 rounded-full text-xs font-medium transition capitalize ${
                  filterCategory === cat
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Filtro por tipo */}
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500 self-center mr-2">Tipo:</span>
          {[
            { value: 'todos', label: 'Todos', icon: '📦', count: counts.todos },
            { value: 'general', label: 'General', icon: '🛒', count: counts.general },
            { value: 'wholesale', label: 'Solo Mayorista', icon: '🏪', count: counts.wholesale }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilterType(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1 ${
                filterType === f.value
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>{f.icon}</span>
              <span>{f.label}</span>
              <span className="opacity-70">({f.count})</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          <div className="text-6xl mb-4">📦</div>
          <p className="text-lg font-medium">No hay productos</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contenido</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(product => (
                  <tr key={product.id} className={`hover:bg-gray-50 ${product.wholesale_only ? 'bg-green-50/30' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center text-2xl flex-shrink-0">
                          {product.icon || getIconForCategory(product.category)}
                        </div>
                        <div>
                          <div className="font-medium">{product.name}</div>
                          <div className="text-xs text-gray-500 truncate max-w-xs">{product.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {product.wholesale_only ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                          🏪 Solo mayorista
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                          📦 General
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gold">
                        ${formatPrice(product.price_usd)}
                      </div>
                      {product.price_wholesale_usd && (
                        <div className="text-xs text-green-600">
                          Mayor: ${formatPrice(product.price_wholesale_usd)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {product.units_per_package > 1 ? (
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                          📦 {product.units_per_package} unidades
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Unidad</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleAvailability(product)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          product.is_available === 1
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {product.is_available === 1 ? '✅ Disponible' : '❌ Agotado'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleOpenModal(product)}
                        className="text-blue-500 hover:text-blue-700 p-2"
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="text-red-500 hover:text-red-700 p-2"
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== MODAL CREAR/EDITAR ===== */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex justify-between items-center z-10">
              <h2 className="text-xl md:text-2xl font-serif font-bold">
                {editingProduct ? '✏️ Editar Producto' : '➕ Nuevo Producto'}
              </h2>
              <button onClick={handleCloseModal} className="text-2xl hover:text-gold p-2">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Preview del icono */}
              <div className="flex items-center gap-4 p-4 bg-background-alt rounded-xl">
                <div className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center text-5xl shadow-sm border-2 border-gold">
                  {formData.icon}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-700 mb-1">Icono del producto</div>
                  <div className="text-xs text-gray-500">Selecciona un emoji</div>
                </div>
              </div>

              {/* Selector de iconos */}
              <div>
                <label className="block text-sm font-medium mb-2">Selecciona un icono</label>
                <div className="mb-3">
                  <div className="flex flex-wrap gap-2">
                    {(iconOptions[formData.category] || allIcons.slice(0, 5)).map((icon, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        className={`w-12 h-12 rounded-xl text-2xl transition flex items-center justify-center ${
                          formData.icon === icon ? 'bg-gold text-white shadow-lg scale-110' : 'bg-gray-100 hover:bg-gold/20'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                <details className="mt-2">
                  <summary className="text-xs text-gold cursor-pointer hover:underline">
                    Ver todos los iconos
                  </summary>
                  <div className="flex flex-wrap gap-2 mt-3 p-3 bg-gray-50 rounded-xl max-h-48 overflow-y-auto">
                    {allIcons.map((icon, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        className={`w-10 h-10 rounded-lg text-xl transition flex items-center justify-center ${
                          formData.icon === icon ? 'bg-gold text-white scale-110' : 'bg-white hover:bg-gold/20'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </details>
              </div>

              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium mb-1">Nombre *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                  placeholder="Ej: Bandeja de Brownies (20 unidades)"
                  required
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium mb-1">Descripción *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                  rows="3"
                  placeholder="Ej: 20 brownies de chocolate belga en bandeja"
                  required
                />
              </div>

              {/* ===== TIPO DE PRODUCTO ===== */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="wholesale_only"
                    checked={formData.wholesale_only}
                    onChange={handleChange}
                    className="w-5 h-5 text-green-500 rounded focus:ring-green-500 mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-green-800">
                      🏪 Producto exclusivo para mayoristas
                    </div>
                    <div className="text-xs text-green-700 mt-1">
                      Solo los clientes mayoristas podrán ver y comprar este producto
                    </div>
                  </div>
                </label>
              </div>

              {/* ===== UNIDADES POR PAQUETE ===== */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  📦 ¿Cuántas unidades trae este producto?
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    name="units_per_package"
                    value={formData.units_per_package}
                    onChange={handleChange}
                    min="1"
                    className="w-32 px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-lg font-semibold"
                  />
                  <div className="text-sm text-blue-700">
                    {formData.units_per_package > 1 ? (
                      <>✅ 1 unidad de venta = <strong>{formData.units_per_package} productos</strong></>
                    ) : (
                      <>✅ Se vende por unidad individual</>
                    )}
                  </div>
                </div>
                <div className="text-xs text-blue-600 mt-2">
                  Ejemplo: Una bandeja de brownies = 20 unidades. El cliente compra 1 = lleva 20 brownies.
                </div>
              </div>

              {/* Precios */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Precio {formData.units_per_package > 1 ? 'por paquete' : 'unitario'} (USD) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="price_usd"
                    value={formData.price_usd}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                    placeholder="Ej: 20.00"
                    required
                  />
                  {formData.units_per_package > 1 && formData.price_usd && (
                    <p className="text-xs text-gray-500 mt-1">
                      Precio por unidad: ${(parseFloat(formData.price_usd) / formData.units_per_package).toFixed(2)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Precio Mayorista (USD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="price_wholesale_usd"
                    value={formData.price_wholesale_usd}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                    placeholder="Ej: 15.00 (opcional)"
                  />
                </div>
              </div>

              {/* Cantidad mínima mayorista */}
              {formData.wholesale_only && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Cantidad mínima de compra
                  </label>
                  <input
                    type="number"
                    name="min_wholesale_qty"
                    value={formData.min_wholesale_qty}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                    placeholder="1"
                  />
                </div>
              )}

              {/* Categoría y Badge */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Categoría</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleCategoryChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                  >
                    <option value="pizza">🍕 Pizza</option>
                    <option value="brownie">🍫 Brownie</option>
                    <option value="galleta">🍪 Galleta</option>
                    <option value="pan">🥖 Pan</option>
                    <option value="croissant">🥐 Croissant</option>
                    <option value="otros">🍽️ Otros</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Badge</label>
                  <input
                    type="text"
                    name="badge"
                    value={formData.badge}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                    placeholder="🔥 Popular"
                  />
                </div>
              </div>

              {/* Preparación e Ingredientes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Prep. (min)</label>
                  <input
                    type="number"
                    name="preparation_time"
                    value={formData.preparation_time}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ingredientes</label>
                  <input
                    type="text"
                    name="ingredients"
                    value={formData.ingredients}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold"
                    placeholder="Chocolate, Nueces..."
                  />
                </div>
              </div>

              {/* Disponible */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_available"
                  checked={formData.is_available}
                  onChange={handleChange}
                  className="w-5 h-5 text-gold rounded focus:ring-gold"
                />
                <span>Disponible para venta</span>
              </label>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 py-3 border rounded-full hover:bg-gray-50 transition font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-gold text-white rounded-full hover:bg-gold-dark transition font-semibold"
                >
                  {editingProduct ? 'Guardar cambios' : 'Crear producto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
